import { useNavigate } from "@tanstack/react-router"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "design-system/molecules/Tabs"
import { Form } from "design-system/organisms/Form"
import { cn } from "design-system/utils"
import { AnimatePresence, motion } from "framer-motion"
import { getErrorTranslations } from "lib/i18n"
import {
  FileText,
  LinkIcon,
  Mail,
  MessageCircle,
  MessageSquareMore,
  PencilLine,
  QrCode,
  Send,
  Server,
  Settings2,
  SquareDashedBottomCode,
  Store,
  Target,
  TriangleAlert,
  WebhookIcon,
  type LucideIcon,
} from "lucide-react"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import { useCampaignQuery } from "modules/campaigns/hooks/useCampaignQuery"
import {
  getMissingFieldCountByTab,
  isConfigurationTabValue,
  isWhatsappTemplateTrigger,
  isWhatsappVisualTrigger,
  shouldShowChannelConfigTab,
  shouldShowTriggerConfigTab,
  type ConfigurationTabValue,
} from "modules/campaigns/lib/configurationTabs"
import { Loading } from "modules/shared/components/Loading"
import { BigBlueIcon } from "modules/shared/icons/bigblue"
import { useEffect, useMemo, useState, type ComponentType, type SVGProps } from "react"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { CampaignDetailResponseDataSchema, CampaignTriggerTypeEnum, ChannelEnum } from "schema"
import { z } from "zod"
import { BriefSubTab } from "./BriefSubTab"
import { ChannelConfigSubTab } from "./ChannelConfigSubTab"
import { GoalsSubTab } from "./GoalsSubTab"
import { OpeningMessagesSubTab } from "./OpeningMessagesSubTab"
import { SetupSubTab } from "./SetupSubTab"
import { TemplatesSubTab } from "./TemplatesSubTab"
import { TriggerConfigSubTab } from "./TriggerConfigSubTab"

type Props = {
  campaignId: string
  isEditable: boolean
  section?: string
}

export function Configuration({ campaignId, isEditable, section }: Props) {
  const { data: campaign, isPending } = useCampaignQuery(campaignId)

  if (isPending || !campaign) return <Loading />

  return <ConfigurationContent campaign={campaign} isEditable={isEditable} section={section} />
}

type TabDef = {
  value: ConfigurationTabValue
  name: string
  icon: ComponentType<SVGProps<SVGSVGElement>>
  visible: boolean
  pendingCount: number
}

type ConfigurationContentProps = {
  campaign: z.infer<typeof CampaignDetailResponseDataSchema>
  isEditable: boolean
  section?: string
}

function ConfigurationContent({ campaign, isEditable, section }: ConfigurationContentProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const form = useFormContext<z.infer<typeof UpdateCampaignSchemaFrontend>>()

  const watchedValues = form.watch()
  const { flow, channel, trigger } = watchedValues

  const pendingCountByTab = useMemo(
    () => getMissingFieldCountByTab(watchedValues),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(watchedValues)],
  )

  const isOutbound = flow === "Outbound"

  const showChannelTab = shouldShowChannelConfigTab(channel)
  const showTriggerTab = shouldShowTriggerConfigTab(trigger)
  const showOpeningMessagesTab = isWhatsappVisualTrigger(channel, trigger)
  const showTemplatesTab = isWhatsappTemplateTrigger(channel, trigger)

  const channelTabName = (() => {
    if (channel === ChannelEnum.Api) return t("campaigns.configuration.tabs.channel.api")
    if (channel === ChannelEnum.Gmail) return t("campaigns.configuration.tabs.channel.gmail")
    if (channel === ChannelEnum.Outlook) return t("campaigns.configuration.tabs.channel.outlook")
    return ""
  })()

  const channelTabIcon: LucideIcon = (() => {
    if (channel === ChannelEnum.Gmail) return Mail
    if (channel === ChannelEnum.Outlook) return Mail
    return Server
  })()

  const triggerTabName = (() => {
    if (trigger === CampaignTriggerTypeEnum.QR) return t("campaigns.configuration.tabs.trigger.qr")
    if (trigger === CampaignTriggerTypeEnum.InstagramDirectMessage)
      return t("campaigns.configuration.tabs.trigger.instagramDirectMessage")
    if (trigger === CampaignTriggerTypeEnum.InstagramCommentReply)
      return t("campaigns.configuration.tabs.trigger.instagramCommentReply")
    if (trigger === CampaignTriggerTypeEnum.Link)
      return t("campaigns.configuration.tabs.trigger.link")
    if (trigger === CampaignTriggerTypeEnum.Widget)
      return t("campaigns.configuration.tabs.trigger.widget")
    if (trigger === CampaignTriggerTypeEnum.Webhook)
      return t("campaigns.configuration.tabs.trigger.webhook")
    if (trigger === CampaignTriggerTypeEnum.Shopify)
      return t("campaigns.configuration.tabs.trigger.shopify")
    if (trigger === CampaignTriggerTypeEnum.BigBlue)
      return t("campaigns.configuration.tabs.trigger.bigblue")
    return ""
  })()

  const triggerTabIcon: ComponentType<SVGProps<SVGSVGElement>> = (() => {
    if (trigger === CampaignTriggerTypeEnum.QR) return QrCode
    if (trigger === CampaignTriggerTypeEnum.InstagramDirectMessage) return Send
    if (trigger === CampaignTriggerTypeEnum.InstagramCommentReply) return MessageSquareMore
    if (trigger === CampaignTriggerTypeEnum.Link) return LinkIcon
    if (trigger === CampaignTriggerTypeEnum.Widget) return SquareDashedBottomCode
    if (trigger === CampaignTriggerTypeEnum.Webhook) return WebhookIcon
    if (trigger === CampaignTriggerTypeEnum.Shopify) return Store
    if (trigger === CampaignTriggerTypeEnum.BigBlue) return BigBlueIcon
    return Settings2
  })()

  const tabs: TabDef[] = [
    {
      value: "brief",
      name: t("campaigns.configuration.tabs.brief"),
      icon: PencilLine,
      visible: true,
      pendingCount: pendingCountByTab.get("brief") ?? 0,
    },
    {
      value: "setup",
      name: t("campaigns.configuration.tabs.setup"),
      icon: Settings2,
      visible: true,
      pendingCount: pendingCountByTab.get("setup") ?? 0,
    },
    {
      value: "channel",
      name: channelTabName,
      icon: channelTabIcon,
      visible: showChannelTab,
      pendingCount: pendingCountByTab.get("channel") ?? 0,
    },
    {
      value: "trigger",
      name: triggerTabName,
      icon: triggerTabIcon,
      visible: showTriggerTab,
      pendingCount: pendingCountByTab.get("trigger") ?? 0,
    },
    {
      value: "openingMessages",
      name: t("campaigns.configuration.tabs.openingMessages"),
      icon: MessageCircle,
      visible: showOpeningMessagesTab,
      pendingCount: pendingCountByTab.get("openingMessages") ?? 0,
    },
    {
      value: "templates",
      name: t("campaigns.configuration.tabs.templates"),
      icon: FileText,
      visible: showTemplatesTab,
      pendingCount: pendingCountByTab.get("templates") ?? 0,
    },
    {
      value: "goals",
      name: t("campaigns.configuration.tabs.goals"),
      icon: Target,
      visible: isOutbound,
      pendingCount: pendingCountByTab.get("goals") ?? 0,
    },
  ]

  const visibleValues = useMemo(() => {
    const set = new Set<ConfigurationTabValue>(["brief", "setup"])
    if (showChannelTab) set.add("channel")
    if (showTriggerTab) set.add("trigger")
    if (showOpeningMessagesTab) set.add("openingMessages")
    if (showTemplatesTab) set.add("templates")
    if (isOutbound) {
      set.add("goals")
    }
    return set
  }, [showChannelTab, showTriggerTab, showOpeningMessagesTab, showTemplatesTab, isOutbound])

  const initialActiveTab: ConfigurationTabValue =
    section && isConfigurationTabValue(section) && visibleValues.has(section) ? section : "brief"
  const [activeTab, setActiveTab] = useState<ConfigurationTabValue>(initialActiveTab)

  useEffect(() => {
    if (!visibleValues.has(activeTab)) {
      setActiveTab("brief")
      navigate({ to: ".", search: { section: "brief" } })
    }
  }, [activeTab, visibleValues, navigate])

  return (
    <Form {...form} errorTranslations={getErrorTranslations()}>
      <Tabs
        variant="pill"
        orientation="vertical"
        value={activeTab}
        onValueChange={(value) => {
          if (!isConfigurationTabValue(value)) return
          setActiveTab(value)
          navigate({ to: ".", search: { section: value } })
        }}
        className="flex lg:flex-row flex-col items-start gap-6"
      >
        <TabsList className="max-w-full h-auto sticky top-20 lg:flex-col gap-1 p-0 bg-background shrink-0 flex flex-row items-start lg:items-stretch justify-start overflow-x-auto overflow-y-hidden md:overflow-visible z-20">
          <AnimatePresence initial={false}>
            {tabs.map((tab) =>
              tab.visible ? (
                <motion.div
                  key={tab.value}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{
                    duration: 0.22,
                    ease: [0.4, 0.0, 0.2, 1],
                  }}
                  className="w-auto lg:w-full origin-top overflow-hidden shrink-0"
                >
                  <TabsTrigger
                    value={tab.value}
                    className={cn(
                      "min-w-fit lg:min-w-48 w-auto lg:w-full h-auto data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-3 py-1.5 flex items-center justify-between gap-2.5",
                    )}
                  >
                    <div className="flex items-center gap-2.5">
                      <tab.icon className="h-5 w-5" />
                      <span>{tab.name}</span>
                    </div>
                    {tab.pendingCount > 0 ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="flex shrink-0 items-center text-warning">
                            <TriangleAlert className="size-4.5" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {t("campaigns.configuration.tabs.pendingFields", {
                            count: tab.pendingCount,
                          })}
                        </TooltipContent>
                      </Tooltip>
                    ) : null}
                  </TabsTrigger>
                </motion.div>
              ) : null,
            )}
          </AnimatePresence>
        </TabsList>
        <div className="h-auto w-full">
          <TabsContent value="brief" className="h-full w-full max-w-7xl">
            <BriefSubTab />
          </TabsContent>
          <TabsContent value="setup" className="h-full w-full max-w-7xl">
            <SetupSubTab isEditable={isEditable} />
          </TabsContent>
          <TabsContent value="channel" className="h-full w-full max-w-7xl">
            <ChannelConfigSubTab isEditable={isEditable} campaign={campaign} />
          </TabsContent>
          <TabsContent value="trigger" className="h-full w-full max-w-7xl">
            <TriggerConfigSubTab isEditable={isEditable} campaign={campaign} />
          </TabsContent>
          <TabsContent value="openingMessages" className="h-full w-full max-w-7xl">
            <OpeningMessagesSubTab isEditable={isEditable} campaign={campaign} />
          </TabsContent>
          <TabsContent value="templates" className="h-full w-full max-w-7xl">
            <TemplatesSubTab isEditable={isEditable} campaign={campaign} />
          </TabsContent>
          <TabsContent value="goals" className="h-full w-full max-w-7xl">
            <GoalsSubTab isEditable={isEditable} />
          </TabsContent>
        </div>
      </Tabs>
    </Form>
  )
}
